// libs/core/src/lib/lit/directives/observe-directive.ts
import { AsyncDirective } from "lit/async-directive.js";
import { directive } from "lit/directive.js";
import { asapScheduler, observeOn } from "rxjs";
var ObserveDirective = class extends AsyncDirective {
  render(observable, initValue) {
    if (this.subscription) {
      return this.lastValue ?? initValue ?? "";
    }
    this.subscription = observable.pipe(observeOn(asapScheduler)).subscribe((value) => {
      this.lastValue = value;
      this.setValue(value);
    });
    return initValue ?? "";
  }
  disconnected() {
    this.subscription?.unsubscribe();
  }
};
var observe = directive(ObserveDirective);

// libs/core/src/lib/lit/directives/async-directive.ts
import { AsyncDirective as LitAsyncDirective } from "lit/async-directive.js";
import { directive as directive2 } from "lit/directive.js";
var AsyncDirective2 = class extends LitAsyncDirective {
  constructor() {
    super(...arguments);
    this.isDisconnected = false;
    this.handler = (value) => {
      if (this.isDisconnected) return;
      this.setValue(value);
    };
  }
  render(pending, initValue) {
    pending.then(this.handler);
    return initValue ?? "";
  }
  disconnected() {
    this.isDisconnected = true;
  }
};
var async = directive2(AsyncDirective2);

// libs/core/src/lib/lit/directives/animation-map.ts
import { directive as directive3, Directive } from "lit/directive.js";
import { html, render } from "lit";

// libs/core/src/lib/lit/append-style.ts
function appendStyle(target, styles) {
  for (const style in styles) {
    target.style[style] = styles[style] ?? "";
  }
}

// libs/core/src/lib/lit/directives/animation-map.ts
var AnimationMap = class extends Directive {
  constructor(partInfo) {
    super(partInfo);
    this.view = document.createElement("div");
    this.lock = false;
    appendStyle(this.view, {
      display: "flex",
      gap: "8px",
      position: "relative"
    });
  }
  render(items, controller) {
    this.applyDirection(controller.direction);
    if (this.lock) {
      return html`${this.view}`;
    }
    if (!this.view.children.length) {
      this.addedElementsWithoutAnimation(items, controller);
      return html`${this.view}`;
    }
    this.transition(items, controller).catch(console.error);
    return html`${this.view}`;
  }
  async transition(items, controller) {
    const [keyMap, indexMap] = this.getKeyMapAndIndexMap(items, controller);
    const viewIndexMap = this.getIndexMapFromView();
    const renderCandidate = this.getAllRenderCandidate(viewIndexMap, indexMap);
    const removeCandidate = this.getAllRemoveCandidate(viewIndexMap, indexMap);
    const moveCandidate = this.getAllMoveCandidate(viewIndexMap, indexMap);
    const renderCandidateElements = this.getAllRenderCandidateElements(renderCandidate, keyMap, controller);
    const removeCandidateElements = this.getAllCandidateElements(removeCandidate);
    const moveCandidateElements = this.getAllCandidateElements(moveCandidate);
    const renderCandidateNodeIndexMap = /* @__PURE__ */ new Map();
    const removeCandidateNodeIndexMap = /* @__PURE__ */ new Map();
    const moveCandidateNodeIndexMap = /* @__PURE__ */ new Map();
    renderCandidateElements.forEach((element, key) => {
      renderCandidateNodeIndexMap.set(indexMap.get(key), element);
    });
    removeCandidateElements.forEach((element, key) => {
      removeCandidateNodeIndexMap.set(viewIndexMap.get(key), element);
    });
    moveCandidateElements.forEach((element, key) => {
      moveCandidateNodeIndexMap.set([viewIndexMap.get(key), indexMap.get(key)], element);
    });
    const renderCandidateNodeEntries = [...renderCandidateElements.entries()];
    const removeCandidateNodeEntries = [...removeCandidateElements.entries()];
    const moveCandidateNodeEntries = [...moveCandidateElements.entries()];
    await controller.onAnimationStart?.();
    const [
      _,
      renderResultState,
      removeResultState,
      moveResultState
    ] = await Promise.all([
      controller.onBeforeAnimation?.(this.view, renderCandidateNodeIndexMap, removeCandidateNodeIndexMap, moveCandidateNodeIndexMap),
      this.executeHandlerAndReturnResultMap(renderCandidateNodeEntries, (node, key) => controller.onBeforeRenderAnimateItem(node, indexMap.get(key))),
      this.executeHandlerAndReturnResultMap(removeCandidateNodeEntries, (node, key) => controller.onBeforeRemoveAnimateItem(node, viewIndexMap.get(key))),
      this.executeHandlerAndReturnResultMap(moveCandidateNodeEntries, (node, key) => controller.onBeforeMoveAnimationItem?.(
        node,
        viewIndexMap.get(key),
        indexMap.get(key)
      ) ?? Promise.resolve())
    ]);
    this.renderNode(renderCandidateElements, indexMap);
    this.removeNode(removeCandidateElements);
    this.moveNode(moveCandidateElements, viewIndexMap, indexMap);
    await Promise.all([
      this.executeHandlerAndReturnResultMap(renderCandidateNodeEntries, (node, key) => controller.onAfterRenderAnimateItem?.(node, indexMap.get(key), renderResultState.get(key)) ?? Promise.resolve()),
      this.executeHandlerAndReturnResultMap(removeCandidateNodeEntries, (node, key) => controller.onAfterRemoveAnimateItem?.(node, viewIndexMap.get(key), removeResultState.get(key)) ?? Promise.resolve()),
      this.executeHandlerAndReturnResultMap(moveCandidateNodeEntries, (node, key) => controller.onAfterMoveAnimationItem?.(
        node,
        moveResultState.get(key)
      ) ?? Promise.resolve()),
      controller.onAfterAnimation?.(this.view, renderCandidateNodeIndexMap, removeCandidateNodeIndexMap, moveCandidateNodeIndexMap)
    ]);
    await controller.onAnimationComplete?.();
  }
  async executeHandlerAndReturnResultMap(nodes, handler) {
    const results = /* @__PURE__ */ new Map();
    await Promise.all(nodes.map(async ([key, node]) => {
      const result = await handler(node, key);
      results.set(key, result);
    }));
    return results;
  }
  getAllRenderCandidate(indexMap, newIndexMap) {
    const candidates = /* @__PURE__ */ new Set();
    newIndexMap.forEach((_, key) => {
      if (!indexMap.has(key)) {
        candidates.add(key);
      }
    });
    return candidates;
  }
  getAllRemoveCandidate(indexMap, newIndexMap) {
    const candidates = /* @__PURE__ */ new Set();
    indexMap.forEach((_, key) => {
      if (!newIndexMap.has(key)) {
        candidates.add(key);
      }
    });
    return candidates;
  }
  getAllMoveCandidate(indexMap, newIndexMap) {
    const candidates = /* @__PURE__ */ new Set();
    const keys = /* @__PURE__ */ new Set([...indexMap.keys(), ...newIndexMap.keys()]);
    keys.forEach((key) => {
      const oldIndex = indexMap.get(key);
      const newIndex = newIndexMap.get(key);
      if (oldIndex === void 0 || newIndex === void 0) return;
      if (oldIndex === newIndex) return;
      candidates.add(key);
    });
    return candidates;
  }
  getAllRenderCandidateElements(candidateSet, keyMap, controller) {
    const candidateMap = /* @__PURE__ */ new Map();
    let index = 0;
    candidateSet.forEach((key) => {
      const value = keyMap.get(key);
      if (!value) return;
      const element = this.createItem(key);
      const template = controller.onTemplateBuilder(value, index);
      render(template, element);
      candidateMap.set(key, element);
      index++;
    });
    return candidateMap;
  }
  getAllCandidateElements(candidateSet) {
    const candidateMap = /* @__PURE__ */ new Map();
    candidateSet.forEach((key) => {
      const element = this.view.querySelector(`#${key}`);
      if (!element) return;
      candidateMap.set(key, element);
    });
    return candidateMap;
  }
  renderNode(candidateElements, indexMap) {
    candidateElements.forEach((node, key) => {
      const index = indexMap.get(key) ?? 0;
      const referenceNode = this.view.children[index];
      if (referenceNode) {
        this.view.insertBefore(node, referenceNode);
      } else {
        this.view.appendChild(node);
      }
    });
  }
  removeNode(nodeMap) {
    nodeMap.forEach((node) => {
      node.remove();
    });
  }
  moveNode(candidateElements, oldIndexMap, newIndexMap) {
    const viewIndexMap = this.getIndexMapFromView();
    candidateElements.forEach((node, key) => {
      const oldIndex = oldIndexMap.get(key);
      const newIndex = newIndexMap.get(key);
      const currentIndex = viewIndexMap.get(key);
      if (currentIndex === newIndex) {
        return;
      }
      let referenceNode;
      if (oldIndex > newIndex) {
        referenceNode = this.view.children[newIndex];
      }
      if (oldIndex < newIndex) {
        referenceNode = this.view.children[oldIndex];
      }
      if (!referenceNode) return;
      this.view.insertBefore(node, referenceNode);
    });
  }
  getKeyMapAndIndexMap(items, controller) {
    const keyMap = /* @__PURE__ */ new Map();
    const indexMap = /* @__PURE__ */ new Map();
    let index = 0;
    for (const item of items) {
      const key = controller.onKeyExtractor(item, index);
      keyMap.set(key, item);
      indexMap.set(key, index);
      index++;
    }
    return [keyMap, indexMap];
  }
  getIndexMapFromView() {
    const indexMap = /* @__PURE__ */ new Map();
    let index = 0;
    this.view.childNodes.forEach((node) => {
      indexMap.set(node.id, index++);
    });
    return indexMap;
  }
  addedElementsWithoutAnimation(items, controller) {
    let index = 0;
    for (const item of items) {
      const key = controller.onKeyExtractor(item, index);
      const element = this.createItem(key);
      const template = controller.onTemplateBuilder(item, index);
      render(template, element);
      this.view.appendChild(element);
      index++;
    }
  }
  createItem(key) {
    const element = document.createElement("div");
    element.id = key.toString();
    return element;
  }
  applyDirection(direction) {
    let flexDirection;
    if (direction === "vertical") {
      flexDirection = "column";
    }
    if (direction === "horizontal") {
      flexDirection = "row";
    }
    appendStyle(this.view, {
      flexDirection
    });
  }
};
var animationMap = directive3(AnimationMap);

// libs/core/src/lib/lit/dom.utils.ts
function createAndAppendInHeaderElement(tagName, sideEffect) {
  const el = document.createElement(tagName);
  if (sideEffect && typeof sideEffect === "function") {
    try {
      sideEffect(el);
    } catch (error) {
      console.error("call side effect error", error);
    }
  }
  document.head.appendChild(el);
  return el;
}
function createAndApplyStyle(css) {
  createAndAppendInHeaderElement("style", (el) => applyStyle(el, css));
}
function applyStyle(target, css) {
  target.textContent = css.cssText;
}
function buildEvent(type, value, event) {
  return new CustomEvent(type, { bubbles: true, composed: true, detail: { value, event } });
}
function dispatchEvent(ctx, type, value, event) {
  ctx.dispatchEvent(buildEvent(type, value, event));
}
function vibrate(pattern = 40) {
  try {
    navigator.vibrate(pattern);
  } catch (error) {
  }
}

// libs/core/src/lib/lit/match-media.ts
import { css as LitCss, unsafeCSS } from "lit";

// libs/core/src/lib/lit/subscribe-reactive-controller.ts
import { combineLatest } from "rxjs";
var defaultConfig = {
  requestUpdate: true
};
var SubscribeReactiveController = class {
  constructor(host, observers, config) {
    this.host = host;
    this.observers = observers;
    host.addController(this);
    this.config = { ...defaultConfig, ...config };
    this.subscribe();
  }
  hostDisconnected() {
    this.subscription?.unsubscribe();
  }
  subscribe() {
    const observable = combineLatest(Array.isArray(this.observers) ? this.observers : [this.observers]);
    this.subscription = observable.subscribe(() => {
      if (!this.config.requestUpdate) return;
      this.host.requestUpdate();
    });
  }
};
function subscribe(host, observers, config) {
  return new SubscribeReactiveController(host, observers, config);
}

// libs/core/src/lib/lit/match-media.ts
import { defer, fromEvent, shareReplay, startWith, map } from "rxjs";
var mobileMediaString = "screen and (max-width: 610px)";
var mobileMatchMedia;
function getMobileMatchMedia() {
  if (!mobileMatchMedia) {
    mobileMatchMedia = matchMedia(mobileMediaString);
  }
  return mobileMatchMedia;
}
function mobileMediaCSS(style) {
  return LitCss`
      @media ${unsafeCSS(mobileMediaString)} {
          ${unsafeCSS(style)}
      }
  `;
}
var emitter$ = defer(() => fromEvent(getMobileMatchMedia(), "change")).pipe(
  map(() => getMobileMatchMedia()),
  startWith(getMobileMatchMedia()),
  shareReplay({ bufferSize: 0, refCount: true })
);
function changeMobileMatchMedia(context) {
  subscribe(context, [emitter$]);
}
function getMobileMatchMediaEmitter() {
  return emitter$;
}
function getMobileMatchMediaAndSubscribe(context) {
  changeMobileMatchMedia(context);
  return getMobileMatchMedia();
}

// libs/core/src/lib/lit/platform.ts
import { unsafeCSS as unsafeCSS2 } from "lit";
function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("crios");
}
function isStandalone() {
  return window?.navigator?.standalone ?? false;
}
function safariPWACss(style) {
  if (isSafari() && isStandalone()) {
    return unsafeCSS2(style);
  }
  return unsafeCSS2("");
}

// libs/core/src/lib/lit/rx-resize-observer.ts
import { Observable as Observable4 } from "rxjs";
var storage = /* @__PURE__ */ new WeakMap();
function resizeObserver(element) {
  if (storage.has(element)) {
    return storage.get(element);
  }
  const observer = new Observable4((subscriber) => {
    const resize = new ResizeObserver((entries) => subscriber.next(entries[0]));
    resize.observe(element);
    return () => {
      resize.unobserve(element);
    };
  });
  storage.set(element, observer);
  return observer;
}

// libs/core/src/lib/lit/i18n/i18n.ts
import { Locale as Locale2 } from "@one-inch-community/models";

// libs/core/src/lib/lit/i18n/locale.ts
import { Locale } from "@one-inch-community/models";
var rtlLocale = [
  Locale.ar
];
function isRTL(localeCode) {
  return rtlLocale.includes(localeCode);
}

// libs/core/src/lib/lit/i18n/i18n.ts
import { BehaviorSubject, map as map2 } from "rxjs";
var defaultLocaleCode = getDefaultLocaleCode();
var translations = /* @__PURE__ */ new Map();
var currentTranslations$ = new BehaviorSubject(null);
var defaultTranslations;
var currentTranslations;
var currentLocaleCode = Locale2.en;
var lastDefaultTranslationsStorageSize = 0;
var lastCurrentTranslationsStorageSize = 0;
var targetHost = document.querySelector("html");
var localeChange$ = currentTranslations$.pipe(
  map2(() => currentLocaleCode)
);
function setTargetHost(host) {
  targetHost = host;
}
async function addTranslation(translationsRecord) {
  for (const localeCode in translationsRecord) {
    const translation = Reflect.get(translationsRecord, localeCode);
    const storage2 = translations.get(localeCode) ?? [];
    storage2.push(translation);
    translations.set(localeCode, storage2);
  }
  await updateLocaleAndTranslations(currentLocaleCode);
}
async function changeLocaleAndUpdate(localeCode) {
  await updateLocaleAndTranslations(localeCode);
}
function listenChangesByPath(path, context) {
  return currentTranslations$.pipe(
    map2((translations2) => {
      if (!translations2) return "";
      const result = translations2[path] ?? "";
      if (context && result.includes("{{") && result.includes("}}")) {
        return interpolateString(result, context);
      }
      return result;
    })
  );
}
function interpolateString(value, context) {
  let newValue = value;
  for (const key in context) {
    const contextValue = context[key];
    const pattern = `{{${key}}}`;
    newValue = newValue.replaceAll(pattern, `${contextValue}`);
  }
  return newValue;
}
var translationsListToRecord = (translationsList) => translationsList.reduce((record, translations2) => ({ ...record, ...translations2 }), {});
async function updateLocaleAndTranslations(newLocaleCode) {
  targetHost.dir = isRTL(newLocaleCode) ? "rtl" : "ltr";
  await Promise.all([
    updateDefaultTranslationsMap(),
    updateCurrentTranslationsMap(newLocaleCode)
  ]);
  currentLocaleCode = newLocaleCode;
  targetHost.lang = newLocaleCode;
}
async function updateDefaultTranslationsMap() {
  const defaultTranslationsByLocale = translations.get(defaultLocaleCode) ?? [];
  if (defaultTranslationsByLocale.length !== lastDefaultTranslationsStorageSize) {
    const translationsList = await Promise.all(defaultTranslationsByLocale.map((fn) => fn()));
    defaultTranslations = translationsListToRecord(translationsList);
    currentTranslations$.next({ ...defaultTranslations, ...currentTranslations });
    lastDefaultTranslationsStorageSize = defaultTranslationsByLocale.length;
  }
}
async function updateCurrentTranslationsMap(newLocaleCode) {
  const translationsByLocale = translations.get(newLocaleCode) ?? [];
  if (currentLocaleCode !== newLocaleCode || translationsByLocale.length !== lastCurrentTranslationsStorageSize) {
    const translationsList = await Promise.all(translationsByLocale.map((fn) => fn()));
    currentTranslations = translationsListToRecord(translationsList);
    currentTranslations$.next({ ...defaultTranslations, ...currentTranslations });
    lastCurrentTranslationsStorageSize = translationsByLocale.length;
  }
}
function isRTLCurrentLocale() {
  return isRTL(currentLocaleCode);
}
function getDefaultLocaleCode() {
  try {
    const userLocale = navigator.language || navigator.userLanguage;
    const code = userLocale.slice(0, 2);
    if (!(code in Locale2)) return Locale2.en;
    return code;
  } catch (error) {
    return Locale2.en;
  }
}
Reflect.set(window, "changeLocale", changeLocaleAndUpdate);

// libs/core/src/lib/lit/i18n/directives/translate-directive.ts
import { AsyncDirective as LitAsyncDirective2 } from "lit/async-directive.js";
import { directive as directive4 } from "lit/directive.js";
import { asapScheduler as asapScheduler2, observeOn as observeOn2 } from "rxjs";
import { objectsEqual } from "@one-inch-community/core/utils";
var stubText = "";
var TranslateDirective = class extends LitAsyncDirective2 {
  render(path, context) {
    if (this.subscription && this.lastPath === path && objectsEqual(this.lastContext, context)) {
      return this.lastValue ?? stubText;
    } else {
      this.disconnected();
    }
    this.subscription = listenChangesByPath(path, context).pipe(
      observeOn2(asapScheduler2)
    ).subscribe((value) => {
      this.setValue(value);
      this.lastValue = value;
      this.lastPath = path;
      this.lastContext = context;
    });
    return this.lastValue ?? stubText;
  }
  disconnected() {
    this.subscription?.unsubscribe();
  }
};
var translate = directive4(TranslateDirective);

// libs/core/src/lib/lit/i18n/i18n-controller.ts
import { SettingsController } from "@one-inch-community/core/settings";
var I18nController = class {
  constructor() {
    this.i18nSettings = new SettingsController(
      "i18n",
      defaultLocaleCode
    );
  }
  async init() {
    const settingsValue = this.i18nSettings.value ?? defaultLocaleCode;
    await changeLocaleAndUpdate(settingsValue);
  }
  async changeLocale(localeCode) {
    this.i18nSettings.setValue(localeCode);
    return await changeLocaleAndUpdate(localeCode);
  }
};

// libs/core/src/lib/lit/i18n/i18n-embedded-controller.ts
var I18nEmbeddedController = class {
  constructor(localeCode, container) {
    this.localeCode = localeCode;
    setTargetHost(container);
  }
  async init() {
    await changeLocaleAndUpdate(this.localeCode);
  }
  async changeLocale(localeCode) {
    return await changeLocaleAndUpdate(localeCode);
  }
};
export {
  AnimationMap,
  I18nController,
  I18nEmbeddedController,
  addTranslation,
  animationMap,
  appendStyle,
  applyStyle,
  async,
  buildEvent,
  changeLocaleAndUpdate,
  changeMobileMatchMedia,
  createAndAppendInHeaderElement,
  createAndApplyStyle,
  defaultLocaleCode,
  dispatchEvent,
  getMobileMatchMedia,
  getMobileMatchMediaAndSubscribe,
  getMobileMatchMediaEmitter,
  isRTL,
  isRTLCurrentLocale,
  isSafari,
  isStandalone,
  listenChangesByPath,
  localeChange$,
  mobileMediaCSS,
  observe,
  resizeObserver,
  safariPWACss,
  setTargetHost,
  subscribe,
  translate,
  vibrate
};
//# sourceMappingURL=index.esm.js.map
